import {
  type GameStartRequestBody,
  getGameActive,
  getPlayersSearch,
  type LineupEntry,
  type ParkConfig,
  postGameByGameIdEnd,
  type SideType,
} from "@aura-frontend/omnibus-az-client";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { EventForm } from "@/components/EventForm/EventForm";
import {
  EventFormData,
  GameType,
} from "@/components/EventForm/EventForm.schema";
import { PageSpinner } from "@/components/ui/PageSpinner/PageSpinner";
import { useField } from "@/hooks/useComplexes";
import {
  getLineupJerseyNumber,
  useFetchTeamJerseyNumbers,
} from "@/hooks/useTeamJerseyNumbers";
import { ROUTES } from "@/lib/routes";
import { type LiveEventPrefill } from "@/types/eventNavigation";
import {
  formatYearMonth,
  parseTimeToUnixSeconds,
  roundToNearest30m,
} from "@/utils/dateUtils";
import { buildLineupEntries } from "@/utils/lineupUtils";
import { getScorekeepUrl } from "@/utils/scorekeepingUtils";

import { useCreateEvent } from "./NewEvent.queries";
import { useStartLiveEvent } from "./useStartLiveEvent";

function NewEvent() {
  const { complexName, fieldId } = useParams<{
    complexName: string;
    fieldId: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const skipBlocker = useRef(false);

  // Memoize the blocker condition function
  const blockerCondition = useCallback(
    () => isFormDirty && !skipBlocker.current,
    [isFormDirty],
  );
  const blocker = useBlocker(blockerCondition);

  const livePrefill = (
    location.state as { livePrefill?: LiveEventPrefill } | null
  )?.livePrefill;

  const isLive = !!livePrefill;
  const [liveEventType, setLiveEventType] = useState<GameType | undefined>(
    livePrefill?.type === "game" ? "game" : "batting_practice",
  );

  const initialOverviewValues = useMemo(() => {
    if (!livePrefill) return undefined;
    const type: GameType =
      livePrefill.type === "game" ? "game" : "batting_practice";
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    return {
      type,
      startDate,
      startTime: roundToNearest30m(new Date()),
    };
  }, [livePrefill]);

  const { createEvent, isCreatingEvent } = useCreateEvent({
    onSuccess: (machineUuid) => {
      skipBlocker.current = true;
      toast("Event created successfully", { variant: "success" });
      navigate(
        ROUTES.MANAGE_FIELD.replace(":complexName", complexName!).replace(
          ":fieldId",
          machineUuid,
        ),
      );
    },
  });

  const { mutate: startLiveEvent, isPending: isStartingLive } =
    useStartLiveEvent({
      onSuccess: (machineUuid, gameType) => {
        skipBlocker.current = true;
        toast("Event started successfully", { variant: "success" });
        navigate(getScorekeepUrl(machineUuid, gameType ?? "game"));
      },
    });

  const fetchTeamJerseyNumbers = useFetchTeamJerseyNumbers();

  if (!complexName || !fieldId) {
    throw new Error("Missing complexName or fieldId in URL parameters");
  }

  const { field, unifiedConfig, isLoadingField } = useField(fieldId);

  if (!isLoadingField && !field) {
    throw new Error("Field not found");
  }

  const confirm = useConfirmStore((s) => s.confirm);

  // Mirrors yakkertrack-ui's useGetActiveGame: /game/active returns 400 when
  // no game is in progress, so a thrown error means "nothing to overwrite".
  const fetchActiveGame = async (machineUuid: string) => {
    try {
      const { data } = await getGameActive({
        query: { machineUuid },
        throwOnError: true,
      });
      return data?.game ?? null;
    } catch {
      return null;
    }
  };

  // Fetch team roster for practices so we can populate lineups
  const fetchTeamRoster = async (teamUuid: string, visibilityGroup: string) => {
    try {
      const { data } = await getPlayersSearch({
        query: {
          teamId: teamUuid,
          visibilityGroup,
        },
        throwOnError: true,
      });
      return data?.players ?? [];
    } catch (e) {
      return [];
    }
  };

  // Build practice lineup from event data for non-game events
  const buildPracticeLineup = async (
    eventData: EventFormData,
  ): Promise<LineupEntry[]> => {
    const { overview } = eventData;
    const visibilityGroup = (
      eventData as unknown as { practiceTeamVisibilityGroup?: string }
    ).practiceTeamVisibilityGroup;

    if (overview.type === "game" || !overview.team || !visibilityGroup) {
      return [];
    }

    const [teamPlayers, jerseyNumbers] = await Promise.all([
      fetchTeamRoster(overview.team, visibilityGroup),
      fetchTeamJerseyNumbers(overview.team),
    ]);

    if (teamPlayers.length === 0) {
      return [];
    }

    return teamPlayers.map((player, index) => {
      const jerseyNumber = getLineupJerseyNumber(jerseyNumbers, player);
      return {
        externalId: player.external_ids?.[0] ?? "",
        hitSide: player.hit_side as SideType,
        pitchSide: player.pitch_side as SideType,
        playerName: player.known_names?.[0] ?? "",
        ...(jerseyNumber && { jerseyNumber: Number(jerseyNumber) }),
        position: index + 1,
      };
    });
  };

  const handleSubmit = async (data: EventFormData) => {
    const { overview, configuration, lineup, gameTeams } = data;
    const isGame = overview.type === "game";

    if (!overview.startDate) {
      throw new Error("Start date is required to create an event");
    }
    const startDate = overview.startDate;

    const yearMonth = formatYearMonth(startDate);
    const scheduleTimeS = parseTimeToUnixSeconds(startDate, overview.startTime);
    const endTimeS = parseTimeToUnixSeconds(startDate, overview.endTime);

    if (!field) {
      throw new Error("Field data is required to create an event");
    }

    if (isLive) {
      const activeGame = await fetchActiveGame(field.machineUuid);
      if (activeGame?.gameUuid) {
        const activeLabel = activeGame.type === "game" ? "game" : "practice";
        const confirmed = await confirm({
          title: `Active ${activeLabel} in progress`,
          description: `There is already an active ${activeLabel} on this field. Starting a new event will end the current one.`,
          confirmText: `End and start new`,
          confirmButtonVariant: "destructive",
        });
        if (!confirmed) return;
        useConfirmStore.setState({ isPending: true });
        try {
          await postGameByGameIdEnd({
            path: { game_id: activeGame.gameUuid },
            body: {},
            throwOnError: true,
          });
        } catch {
          useConfirmStore.setState({ isOpen: false, isPending: false });
          toast("Failed to end the active event. Please try again.", {
            variant: "error",
          });
          return;
        }
        useConfirmStore.setState({ isOpen: false, isPending: false });
      }

      const parkConfig: ParkConfig = {
        sport: configuration.sport,
        rubberDistanceF: configuration.moundDistance,
        ...(configuration.strikeZone && {
          strikeZone: configuration.strikeZone,
        }),
        field: {
          ageGroup: configuration.level,
          name: field.name,
          diamondSizeFeet: configuration.baseDistance ?? 0,
          infieldAtFoulLineFeet: configuration.infieldAtFoulLine ?? 0,
          fenceFirstBaseFeet: configuration.fenceFirstBase ?? 0,
          fenceThirdBaseFeet: configuration.fenceThirdBase ?? 0,
          fenceLeftFieldFeet: configuration.leftField ?? 0,
          fenceCenterFieldFeet: configuration.centerField ?? 0,
          fenceRightFieldFeet: configuration.rightField ?? 0,
        },
      };

      // For practices with a selected team, fetch the roster to populate lineups
      const practiceHomeTeamLineup = await buildPracticeLineup(data);

      // /game/start takes the colors as flat fields, not a teamColors pair
      const teamColors = {
        ...(data.teamColors?.home && { homeTeamColor: data.teamColors.home }),
        ...(data.teamColors?.away && { awayTeamColor: data.teamColors.away }),
      };
      const gameBody: GameStartRequestBody =
        overview.type === "game"
          ? {
              type: "game",
              machineUuid: field.machineUuid,
              sport: configuration.sport,
              homeTeamId: gameTeams?.home.uuid ?? "",
              awayTeamId: gameTeams?.away.uuid ?? "",
              homeTeamLineup: lineup ? buildLineupEntries(lineup.homeTeam) : [],
              awayTeamLineup: lineup ? buildLineupEntries(lineup.awayTeam) : [],
              ...teamColors,
            }
          : {
              type: overview.type,
              machineUuid: field.machineUuid,
              sport: configuration.sport,
              ...(overview.team ? { homeTeamId: overview.team } : {}),
              homeTeamLineup: practiceHomeTeamLineup,
              awayTeamLineup: [],
              ...teamColors,
            };

      startLiveEvent({
        machineUuid: field.machineUuid,
        parkConfig,
        ledMode: configuration.ledBoardMode,
        ledEndTimeS: endTimeS,
        orgId: overview.orgId ?? null,
        gameBody,
      });
      return;
    }

    // For scheduled practices with a selected team, fetch the roster
    const scheduledPracticeHomeLineup = await buildPracticeLineup(data);

    createEvent({
      body: {
        machine_uuid: field.machineUuid,
        year_month: yearMonth,
        schedule_time_s: scheduleTimeS,
        internal: {
          name: overview.name,
          notes: overview.additionalNotes,
          led: configuration.ledBoardMode,
          ...(overview.orgId && { org_id: overview.orgId }),
          game: {
            type: overview.type,
            generated_display_name:
              overview.type === "game"
                ? `${gameTeams?.home.name || "Home"} x ${gameTeams?.away.name || "Away"}`
                : overview.teamName || overview.name || "Practice",
            machine_uuid: field.machineUuid,
            lineups: {
              homeLineup:
                isGame && lineup
                  ? buildLineupEntries(lineup.homeTeam)
                  : isGame
                    ? []
                    : scheduledPracticeHomeLineup,
              awayLineup:
                isGame && lineup ? buildLineupEntries(lineup.awayTeam) : [],
            },
            sport: configuration.sport,
            teams: {
              home:
                overview.type === "game"
                  ? (gameTeams?.home.uuid ?? "")
                  : (overview.team ?? ""),
              away: isGame ? (gameTeams?.away.uuid ?? "") : "",
            },
            teamNames: {
              home:
                overview.type === "game"
                  ? (gameTeams?.home.name ?? "")
                  : (overview.teamName ?? ""),
              away: isGame ? (gameTeams?.away.name ?? "") : "",
            },
            ...(data.teamColors && { teamColors: data.teamColors }),
            times: [
              {
                startTime: scheduleTimeS,
                endTime: endTimeS,
                machineUuid: field.machineUuid,
              },
            ],
            year_month: yearMonth,
          },
          park: {
            sport: configuration.sport,
            rubberDistanceF: configuration.moundDistance,
            ...(configuration.strikeZone && {
              strikeZone: configuration.strikeZone,
            }),
            field: {
              ageGroup: configuration.level,
              name: field.name,
              diamondSizeFeet: configuration.baseDistance ?? 0,
              infieldAtFoulLineFeet: configuration.infieldAtFoulLine ?? 0,
              fenceFirstBaseFeet: configuration.fenceFirstBase ?? 0,
              fenceThirdBaseFeet: configuration.fenceThirdBase ?? 0,
              fenceLeftFieldFeet: configuration.leftField ?? 0,
              fenceCenterFieldFeet: configuration.centerField ?? 0,
              fenceRightFieldFeet: configuration.rightField ?? 0,
            },
          },
        },
      },
    });
  };

  if (isLoadingField || !field) {
    return <PageSpinner />;
  }

  return (
    <div className="min-h-full flex flex-col">
      <div className="sticky top-0 z-20 bg-white">
        <div className="pt-4 pb-4 h-18 max-w-130 mx-auto px-5 w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div />
          <div className="text-center text-base">
            {`${
              isLive
                ? liveEventType === "game"
                  ? "Start Game"
                  : "Start Practice"
                : "Create Event"
            }: ${liveEventType === "game" ? 3 : 2} Easy Steps`}
          </div>
          <div />
        </div>
      </div>

      <EventForm
        complexName={complexName}
        fieldName={field.name}
        isFieldHealthy={field.status === "active"}
        fieldId={fieldId}
        onSubmit={handleSubmit}
        variant="create"
        onDirtyChange={setIsFormDirty}
        isSubmitting={isCreatingEvent || isStartingLive}
        initialOverviewValues={initialOverviewValues}
        isLive={isLive}
        onEventTypeChange={(type) =>
          setLiveEventType(type as GameType | undefined)
        }
        fieldUnifiedConfig={unifiedConfig}
      />

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => !open && blocker.reset?.()}
        defaultCancelButton={false}
        size="sm"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
              </DialogHeader>
              <p className="text-md ">
                {isLive
                  ? `You've started setting up this ${
                      liveEventType === "game" ? "game" : "practice"
                    }. If you leave now, your progress will be lost and you'll need to start over.`
                  : "You've started creating this event. If you leave now, it will be removed from the field schedule and you'll need to create it again."}
              </p>
            </DialogScrollableContent>
            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  className="underline"
                  onClick={() => blocker.reset?.()}
                >
                  Back
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={() => blocker.proceed?.()}
                >
                  Continue
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

export { NewEvent };
